import { Box } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import colors from "../../../../shared/constants/color";
import { Button } from "../../../../components";
import HeightIcon from "@mui/icons-material/Height";
import { useWiserTheme } from "../../../../hooks/useWiserTheme";
import {
  getHrCommentRootSx,
  getResizeHandleSx,
  getResizeHandleIconSx,
  getHrCommentQuillWrapperSx,
  hrCommentFooterSx,
} from "../../style";

const HR_COMMENT_EDITOR_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "link"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

const HR_COMMENT_EDITOR_FORMATS = [
  "bold",
  "italic",
  "underline",
  "link",
  "list",
  "bullet",
];

interface IProps {
  comment: string;
  onSave?: (comment: string) => void;
}

export const HrCommentCard = ({ comment, onSave }: IProps) => {
  const { darkModeActive, tokens } = useWiserTheme();
  const [localComment, setLocalComment] = useState<string>(comment);
  const [height, setHeight] = useState<number>(window.innerHeight * 0.22);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const startYRef = useRef<number>(0);
  const startHeightRef = useRef<number>(0);

  useEffect(() => {
    setLocalComment(comment);
  }, [comment]);

  const handleSave = () => {
    if (onSave) {
      onSave(localComment);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    startYRef.current = e.clientY;
    startHeightRef.current = height;
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaY = startYRef.current - e.clientY;
        const minHeight = window.innerHeight * 0.18;
        const maxHeight = window.innerHeight * 0.5;
        const newHeight = Math.max(
          minHeight,
          Math.min(maxHeight, startHeightRef.current + deltaY),
        );
        setHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Box sx={getHrCommentRootSx(darkModeActive, height)}>
      <Box
        onMouseDown={handleMouseDown}
        sx={getResizeHandleSx(isDragging, darkModeActive, tokens, "hr")}
      >
        <HeightIcon sx={getResizeHandleIconSx(darkModeActive, tokens, "hr")} />
      </Box>
      <Box sx={getHrCommentQuillWrapperSx(darkModeActive, tokens)}>
        <ReactQuill
          value={localComment}
          onChange={setLocalComment}
          theme="snow"
          modules={HR_COMMENT_EDITOR_MODULES}
          formats={HR_COMMENT_EDITOR_FORMATS}
          placeholder="Yorumunuzu yazın..."
        />
      </Box>
      <Box sx={hrCommentFooterSx}>
        <Button
          variant="contained"
          size="sm"
          color={colors.yellow.Y500}
          fontWeight="400"
          onClick={handleSave}
        >
          Kaydet
        </Button>
      </Box>
    </Box>
  );
};